/**
 * RS_PanelCalculator — Smart Coverage Calculator v2.2
 *
 * 4-step guided purchase flow:
 *   1. Wall Dimensions (height × width, imperial/metric)
 *   2. Panel Size Recommendation (best price efficiency)
 *   3. Quantity + Waste Factor
 *   4. Style Selection + Price Breakdown + Add to Cart
 *
 * Handles two product structures:
 *   A) Size / Quantity / Style — variant is a multi-pack (e.g. "6 Pieces")
 *   B) Size / Style (or Color) — variant is a single panel
 */

if (!customElements.get('rs-panel-calculator')) {
  class RS_PanelCalculator extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 1;
      this.units = localStorage.getItem('rs-calc-units') || 'imperial';
      this.wallHeight = 0;
      this.wallWidth = 0;
      this.selectedSize = null;
      this.wasteFactor = 0.10;
      this.selectedStyle = null;
      this.productData = null;
      this.parsedSizes = [];
      this.parsedStyles = [];
      this.sizeRecommendations = [];
      this.panelsPerVariant = 1;
      this.variantsNeeded = 0;
      this.totalPanels = 0;
      this._singlePanelCount = 0;
      this._singlePanelUnitPrice = null;
      this._singlePanelVariantId = null;
      this._variantSeedObserver = null;
    }

    disconnectedCallback() {
      this._variantSeedObserver?.disconnect();
      this._variantSeedObserver = null;
    }

    /** Step 4 style grid only — single-option products skip style selection but still show price / ATC. */
    _hasStyleOptions() {
      return Array.isArray(this.parsedStyles) && this.parsedStyles.length > 0;
    }

    connectedCallback() {
      this.productData = this._parseProductJSON();
      if (!this.productData) return;

      this._parseVariantOptions();
      this._bindEvents();
      this._initUnitToggle();
      this._renderStep(1);
      this._attachVariantSeedVisibilityObserver();
      queueMicrotask(() => this._seedMainFormVariantIfEmpty());
    }

    /* ── Product Data Parsing ─────────────────────────────────────────── */

    _parseProductJSON() {
      const script = this.querySelector('[data-rs-calc-product]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error('RS_PanelCalculator: Failed to parse product JSON', e);
        return null;
      }
    }

    _parseVariantOptions() {
      const { variants, options } = this.productData;
      const optionNames = options.map(o => o.toLowerCase());

      const sizeIdx = optionNames.indexOf('size');
      const qtyIdx = optionNames.findIndex(
        o =>
          o === 'quantity' ||
          o === 'set' ||
          o === 'pack' ||
          /\b(quantity|set|pack|panel\s*set)\b/i.test(o)
      );
      const styleIdx = optionNames.findIndex(o => o === 'style' || o === 'color');

      const sizeKey = sizeIdx >= 0 ? `option${sizeIdx + 1}` : null;
      const qtyKey = qtyIdx >= 0 ? `option${qtyIdx + 1}` : null;
      const styleKey = styleIdx >= 0 ? `option${styleIdx + 1}` : null;

      this._optionKeys = { sizeKey, qtyKey, styleKey };

      const uniqueSizes = new Set();
      const uniqueStyles = new Set();

      this.panelsPerVariant = 1;

      variants.forEach(v => {
        if (sizeKey && v[sizeKey]) uniqueSizes.add(v[sizeKey]);
        if (styleKey && v[styleKey]) uniqueStyles.add(v[styleKey]);
        if (qtyKey && v[qtyKey]) {
          const count = this._extractPieceCount(v[qtyKey]);
          if (count > this.panelsPerVariant) this.panelsPerVariant = count;
        }
      });

      this._qtyLabel = qtyKey ? variants[0]?.[qtyKey] || `${this.panelsPerVariant} Pieces` : 'Single Panel';

      this.parsedSizes = [...uniqueSizes].map(name => ({
        name,
        ...this._extractPanelDimensions(name)
      }));

      this.parsedStyles = [...uniqueStyles].map(name => {
        const variant = variants.find(v => styleKey && v[styleKey] === name);
        return {
          name,
          image: variant?.featured_image?.src || null
        };
      });
    }

    _extractPanelDimensions(sizeStr) {
      const cmMatch = sizeStr.match(/(\d+(?:\.\d+)?)\s*(?:cm|CM)/g);
      const inchMatch = sizeStr.match(/(\d+(?:\.\d+)?)\s*"/g);

      let widthFt, heightFt, widthCm, heightCm;

      if (cmMatch && cmMatch.length >= 2) {
        widthCm = parseFloat(cmMatch[0]);
        heightCm = parseFloat(cmMatch[1]);
        widthFt = widthCm / 30.48;
        heightFt = heightCm / 30.48;
      } else if (cmMatch && cmMatch.length === 1) {
        widthCm = parseFloat(cmMatch[0]);
        heightCm = widthCm;
        widthFt = widthCm / 30.48;
        heightFt = heightCm / 30.48;
      } else if (inchMatch && inchMatch.length >= 2) {
        widthFt = parseFloat(inchMatch[0]) / 12;
        heightFt = parseFloat(inchMatch[1]) / 12;
        widthCm = widthFt * 30.48;
        heightCm = heightFt * 30.48;
      } else {
        const nums = sizeStr.match(/(\d+(?:\.\d+)?)/g);
        if (nums && nums.length >= 2) {
          widthCm = parseFloat(nums[0]);
          heightCm = parseFloat(nums[1]);
          widthFt = widthCm / 30.48;
          heightFt = heightCm / 30.48;
        } else if (nums && nums.length === 1) {
          widthCm = parseFloat(nums[0]);
          heightCm = widthCm;
          widthFt = widthCm / 30.48;
          heightFt = heightCm / 30.48;
        } else {
          return { widthFt: 0, heightFt: 0, widthCm: 0, heightCm: 0 };
        }
      }

      return {
        widthFt: parseFloat(widthFt.toFixed(4)),
        heightFt: parseFloat(heightFt.toFixed(4)),
        widthCm: parseFloat((widthCm || 0).toFixed(1)),
        heightCm: parseFloat((heightCm || 0).toFixed(1))
      };
    }

    _extractPieceCount(str) {
      const match = str.match(/(\d+)\s*(?:pieces?|panels?|pcs?|pack)/i);
      if (match) return parseInt(match[1], 10);
      if (/single/i.test(str)) return 1;
      const numMatch = str.match(/^(\d+)$/);
      if (numMatch) return parseInt(numMatch[1], 10);
      return 1;
    }

    _resolvePanelSetSelect() {
      const scope =
        this.closest('.product.js-product') ||
        this.closest('.product') ||
        this.closest('.shopify-section') ||
        document;
      const formRoot = scope.querySelector('product-form') || document.querySelector('product-form');
      return (
        formRoot?.querySelector('custom-select[id*="panel-set"]') ||
        document.querySelector('custom-select[id*="panel-set"]')
      );
    }

    /**
     * Pack sizes (1, 3, 6, …) for the optimiser. Reads Panel Set `custom-select` option list when present.
     * Swatch-based PDPs have no `li[role="option"]` under that control — then derive counts from
     * unique `data-set-panels` rows (same source as `_getUniqueOption1Option2PanelsFromHolder`).
     */
    _collectPanelSetPackNumbersForOptimizer(uniquePanels) {
      const fromDom = [];
      const panelSetSelect = this._resolvePanelSetSelect();
      if (panelSetSelect) {
        panelSetSelect.querySelectorAll('li[role="option"]').forEach((li) => {
          const raw = (li.dataset.value || '').trim();
          if (!raw || li.classList.contains('is-nonexistent')) return;
          const n = this._panelSetCountFromDataValue(raw);
          if (n != null) fromDom.push(n);
        });
      }
      if (fromDom.length > 0) {
        return [...new Set(fromDom)].sort((a, b) => b - a);
      }

      const rows = uniquePanels || this._getUniqueOption1Option2PanelsFromHolder();
      const { qtyKey } = this._optionKeys || {};
      const seen = new Set();
      const fromHolder = [];

      for (const p of rows) {
        let raw = null;
        if (qtyKey && p[qtyKey] != null && String(p[qtyKey]).trim() !== '') {
          raw = p[qtyKey];
        } else {
          const o1 = String(p.option1 ?? '');
          const o2 = String(p.option2 ?? '');
          const o1Size = this._looksLikeSizeDimensionString(o1);
          const o2Size = this._looksLikeSizeDimensionString(o2);
          if (o1Size && !o2Size) raw = o2;
          else if (o2Size && !o1Size) raw = o1;
          else raw = o2 || o1;
        }
        const n = raw != null ? this._packCountFromHolderOption(String(raw)) : null;
        if (n != null && Number.isFinite(n) && !seen.has(n)) {
          seen.add(n);
          fromHolder.push(n);
        }
      }
      return fromHolder.sort((a, b) => b - a);
    }

    /** First pack count from option data-value (e.g. "3 Panel Set" → 3, single → 1). */
    _panelSetCountFromDataValue(raw) {
      const t = (raw || '').trim();
      if (!t) return null;
      const m = t.match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
      if (/single/i.test(t)) return 1;
      return null;
    }

    /** True if the option value is a panel size / dimension string (not a pack or material label). */
    _looksLikeSizeDimensionString(t) {
      const s = String(t || '');
      return (
        /\b\d+(?:\.\d+)?\s*[×x]\s*\d+(?:\.\d+)?/i.test(s) ||
        /\bcm\b/i.test(s) ||
        (["″", '"', "'"].some((ch) => s.includes(ch)) && /\d/.test(s)) ||
        /^\s*L\s*\d/i.test(s)
      );
    }

    /**
     * Pack count for matching holder rows / variants: "3 Panel Set" → 3, "Walnut" → 1 (single-SKU line item).
     * Returns null for size-dimension strings so we never treat L12… as pack count 12.
     */
    _packCountFromHolderOption(raw) {
      const t = String(raw ?? '').trim();
      if (!t) return null;
      if (this._looksLikeSizeDimensionString(t)) return null;
      const n = this._panelSetCountFromDataValue(t);
      if (n != null) return n;
      return 1;
    }

    /**
     * When the calculator picks an optimal pack size, align the product Panel Set dropdown
     * (variant picker) if it currently shows a different pack.
     */
    _syncPanelSetVariantPicker(panelSetSelect, targetCount) {
      if (!panelSetSelect || targetCount == null) return;
      const selected = panelSetSelect.querySelector('li[role="option"][aria-selected="true"]');
      const current = selected ? this._panelSetCountFromDataValue(selected.dataset.value) : null;
      if (current === targetCount) return;

      const options = panelSetSelect.querySelectorAll('li[role="option"]');
      let targetLi = null;
      for (let i = 0; i < options.length; i += 1) {
        const li = options[i];
        const raw = (li.dataset.value || '').trim();
        if (!raw || li.classList.contains('is-nonexistent')) continue;
        if (this._panelSetCountFromDataValue(raw) === targetCount) {
          targetLi = li;
          break;
        }
      }
      if (!targetLi || typeof panelSetSelect.selectOption !== 'function') return;
      panelSetSelect.selectOption(targetLi);
    }

    /* ── Event Binding ────────────────────────────────────────────────── */

    _bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-rs-calc-action]');
        if (!target) return;

        switch (target.dataset.rsCalcAction) {
          case 'toggle-unit': this._handleUnitToggle(target); break;
          case 'select-size': this._handleSizeSelect(target); break;
          case 'select-waste': this._handleWasteSelect(target); break;
          case 'select-style': this._handleStyleSelect(target); break;
          case 'continue': this._advanceStep(); break;
          case 'edit-step': this._goToStep(parseInt(target.dataset.rsCalcStep, 10)); break;
          case 'add-to-cart': this._addToCart(); break;
        }
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-rs-calc-input]')) {
          this._handleDimensionInput();
        }
      });
    }

    /* ── Unit Toggle ──────────────────────────────────────────────────── */

    _initUnitToggle() {
      const tabs = this.querySelectorAll('[data-rs-calc-action="toggle-unit"]');
      tabs.forEach(tab => {
        tab.classList.toggle('rs-panel-calc__toggle-tab--active', tab.dataset.rsCalcUnit === this.units);
      });
      this._updateInputLabels();
    }

    _handleUnitToggle(target) {
      const unit = target.dataset.rsCalcUnit;
      if (unit === this.units) return;

      this.units = unit;
      localStorage.setItem('rs-calc-units', unit);

      this.querySelectorAll('[data-rs-calc-action="toggle-unit"]').forEach(t =>
        t.classList.toggle('rs-panel-calc__toggle-tab--active', t.dataset.rsCalcUnit === unit)
      );

      this._convertInputValues();
      this._updateInputLabels();
    }

    _updateInputLabels() {
      const heightLabel = this.querySelector('[data-rs-calc-label="height"]');
      const widthLabel = this.querySelector('[data-rs-calc-label="width"]');
      if (heightLabel) heightLabel.textContent = this.units === 'imperial' ? 'Wall Height (ft)' : 'Wall Height (m)';
      if (widthLabel) widthLabel.textContent = this.units === 'imperial' ? 'Wall Width (ft)' : 'Wall Width (m)';
    }

    _convertInputValues() {
      const hInput = this.querySelector('[data-rs-calc-input="height"]');
      const wInput = this.querySelector('[data-rs-calc-input="width"]');
      if (!hInput || !wInput) return;

      const hVal = parseFloat(hInput.value);
      const wVal = parseFloat(wInput.value);

      if (this.units === 'metric') {
        if (hVal) hInput.value = (hVal * 0.3048).toFixed(2);
        if (wVal) wInput.value = (wVal * 0.3048).toFixed(2);
      } else {
        if (hVal) hInput.value = (hVal / 0.3048).toFixed(2);
        if (wVal) wInput.value = (wVal / 0.3048).toFixed(2);
      }
    }

    /* ── Dimension Input ──────────────────────────────────────────────── */

    _handleDimensionInput() {
      const hInput = this.querySelector('[data-rs-calc-input="height"]');
      const wInput = this.querySelector('[data-rs-calc-input="width"]');

      let h = parseFloat(hInput?.value) || 0;
      let w = parseFloat(wInput?.value) || 0;

      if (this.units === 'metric') {
        h = h / 0.3048;
        w = w / 0.3048;
      }

      this.wallHeight = h;
      this.wallWidth = w;

      if (h > 0 && w > 0 && this.currentStep === 1) {
        this._calculateSizeRecommendations();
      }
    }

    /* ── Size Recommendation (price-efficiency ranked) ────────────────── */

    _calculateSizeRecommendations() {
      const wallArea = this.wallHeight * this.wallWidth;
      if (wallArea <= 0) return;

      this.sizeRecommendations = this.parsedSizes.map(size => {
        const panelArea = size.widthFt * size.heightFt;
        if (panelArea <= 0) return { ...size, panelsNeeded: 0, waste: 0, wastePercent: 0, costPerSqFt: Infinity };

        const panelsNeeded = Math.ceil(wallArea / panelArea);
        const variantsNeeded = Math.ceil(panelsNeeded / this.panelsPerVariant);
        const totalPanels = variantsNeeded * this.panelsPerVariant;
        const totalCoverage = totalPanels * panelArea;
        const waste = totalCoverage - wallArea;
        const wastePercent = (waste / wallArea) * 100;

        const unitPrice = this._getVariantPrice(size.name);
        const totalCost = unitPrice * variantsNeeded;
        const costPerSqFt = totalCoverage > 0 ? totalCost / totalCoverage : Infinity;

        return { ...size, panelsNeeded, variantsNeeded, totalPanels, waste, wastePercent, costPerSqFt, unitPrice };
      });

      this.sizeRecommendations.sort((a, b) => a.costPerSqFt - b.costPerSqFt);
    }

    _getVariantPrice(sizeName) {
      const { sizeKey } = this._optionKeys;
      const variant = this.productData.variants.find(v =>
        (!sizeKey || v[sizeKey] === sizeName) && v.available
      );
      return variant ? variant.price / 100 : 0;
    }

    _handleSizeSelect(target) {
      const sizeName = target.dataset.rsCalcSize;
      this.selectedSize = this.sizeRecommendations.find(s => s.name === sizeName) || null;

      this.querySelectorAll('[data-rs-calc-size]').forEach(card => {
        card.classList.toggle('rs-panel-calc__size-card--selected', card.dataset.rsCalcSize === sizeName);
      });

      if (this.selectedSize) {
        this._calculateQuantity();
        const continueBtn = this.querySelector('[data-rs-calc-step="2"] .rs-panel-calc__continue');
        if (continueBtn) {
          continueBtn.style.opacity = '1';
          continueBtn.disabled = false;
        }
      }
    }

    /* ── Quantity Calculation ──────────────────────────────────────────── */

    _calculateQuantity() {
      if (!this.selectedSize) return;
      // Panel counts & cart quantities come from _updateSetBreakdown (it uses raw panels-after-waste for packing math).
      this._updateSetBreakdown();
    }

    _handleWasteSelect(target) {
      const waste = parseFloat(target.dataset.rsCalcWaste);
      this.wasteFactor = waste;

      this.querySelectorAll('[data-rs-calc-action="select-waste"]').forEach(chip => {
        chip.classList.toggle('rs-panel-calc__waste-chip--selected', parseFloat(chip.dataset.rsCalcWaste) === waste);
      });

      const disclaimer = this.querySelector('[data-rs-calc-disclaimer]');
      if (disclaimer) {
        disclaimer.classList.toggle('rs-panel-calc__disclaimer--visible', waste === 0);
      }

      this._calculateQuantity();
    }

    /* ── Style Selection ──────────────────────────────────────────────── */

    _handleStyleSelect(target) {
      const styleName = target.dataset.rsCalcStyle;
      this.selectedStyle = styleName;

      this.querySelectorAll('[data-rs-calc-style]').forEach(card => {
        card.classList.toggle('rs-panel-calc__style-card--selected', card.dataset.rsCalcStyle === styleName);
      });

      this._updatePriceBreakdown();
      this._updateATCButton();
    }

    /* ── Cart Integration ─────────────────────────────────────────────── */

    async _addToCart() {
      if (!this.selectedSize || this.variantsNeeded <= 0) return;
      if (this._hasStyleOptions() && !this.selectedStyle) return;

      const variant = this._findVariantById(this._getMainFormVariantId());
      const packRow = this._getPackRowForPricingFromHolder(variant);

      if (!variant && !packRow) {
        this._showError('Selected combination is not available. Please try a different style.');
        return;
      }

      const packVariantId = packRow?.id ?? variant?.id;
      const items = [{ id: packVariantId, quantity: this.variantsNeeded }];
      const sc = this._singlePanelCount || 0;
      const singleId = this._singlePanelVariantId;
      if (sc > 0 && singleId) {
        items.push({ id: singleId, quantity: sc });
      }

      let sections = 'cart-icon-bubble';
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionEl = cartDrawer.closest('.shopify-section');
        if (sectionEl?.id) {
          sections += `,${sectionEl.id.replace('shopify-section-', '')}`;
        }
      }

      const addUrl =
        window.Shopify?.routes?.root != null
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

      const atcBtn = this.querySelector('[data-rs-calc-action="add-to-cart"]');
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding...';
      }

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections,
            sections_url: `${window.location.pathname || '/'}?drawer_pass=core`
          })
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const msg =
            typeof data.description === 'string'
              ? data.description
              : data.message || 'Cart add failed';
          throw new Error(msg);
        }

        const openDrawer =
          typeof theme !== 'undefined' &&
          theme.settings &&
          theme.settings.afterAtc === 'drawer';

        if (data.sections && cartDrawer) {
          await cartDrawer.hydrateAfterAdd(data, { openDrawer });
        } else if (data.sections) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && data.sections['cart-icon-bubble']) {
            bubble.innerHTML = data.sections['cart-icon-bubble'];
          }
        } else if (cartDrawer?.refresh) {
          await cartDrawer.refresh(false); 
          if (openDrawer) {
            document.dispatchEvent(
              new CustomEvent('dispatch:cart-drawer:open', {
                bubbles: true,
                detail: { opener: document.getElementById('cart-icon') }
              })
            );
          }
        }

        document.querySelector('product-form')?.dispatchEvent(
          new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart: data, variantId: packVariantId }
          })
        );

        this._showConfirmation();
        this._updateCartCount();
      } catch (err) {
        console.error('RS_PanelCalculator: Cart error', err);
        this._showError('Failed to add items to cart. Please try again.');
      } finally {
        if (atcBtn) {
          atcBtn.disabled = false;
          this._updateATCButton();
        }
      }
    }

    _updateCartCount() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    /* ── Step Machine ─────────────────────────────────────────────────── */

    _advanceStep() {
      if (this.currentStep === 1) {
        if (this.wallHeight <= 0 || this.wallWidth <= 0) {
          this._showError('Please enter both wall height and width.');
          return;
        }
        this._handleDimensionInput();
      }
      if (this.currentStep === 2 && !this.selectedSize) {
        this._showError('Please select a panel size.');
        return;
      }
      if (this.currentStep < 4) {
        this._goToStep(this.currentStep + 1);
      }
    }

    _goToStep(step) {
      this.currentStep = step;
      this._renderStep(step);
    }

    _renderStep(activeStep) {
      for (let i = 1; i <= 4; i++) {
        const stepEl = this.querySelector(`[data-rs-calc-step="${i}"]`);
        if (!stepEl) continue;

        const preview = stepEl.querySelector('[data-rs-calc-step-preview]');
        const content = stepEl.querySelector('[data-rs-calc-step-content]');
        const pending = stepEl.querySelector('[data-rs-calc-step-pending]');

        if (i < activeStep) {
          /* Visited / completed — summary row only */
          stepEl.hidden = false;
          if (preview) preview.style.display = 'flex';
          if (content) content.style.display = 'none';
          if (pending) pending.style.display = 'none';
          this._updateStepPreview(i);
        } else if (i === activeStep) {
          /* Active step — full content */
          stepEl.hidden = false;
          if (preview) preview.style.display = 'none';
          if (content) content.style.display = 'flex';
          if (pending) pending.style.display = 'none';
          if (i === 2) this._renderSizeCards();
          if (i === 3) this._calculateQuantity();
          if (i === 4) {
            this._renderStyleGrid();
            if (!this._hasStyleOptions()) {
              this._updatePriceBreakdown();
              this._updateATCButton();
            }
          }
        } else {
          /* Not active and not visited — hide entire step */
          stepEl.hidden = true;
          if (preview) preview.style.display = 'none';
          if (content) content.style.display = 'none';
          if (pending) pending.style.display = 'none';
        }
      }

      this._updateProgressBar(activeStep);
    }

    _updateProgressBar(activeStep) {
      this.querySelectorAll('[data-rs-calc-tab]').forEach(tab => {
        const tabStep = parseInt(tab.dataset.rsCalcTab, 10);
        tab.classList.remove('rs-panel-calc__progress-tab--completed', 'rs-panel-calc__progress-tab--active', 'rs-panel-calc__progress-tab--pending');

        if (tabStep < activeStep) {
          tab.classList.add('rs-panel-calc__progress-tab--completed');
          if (!tab.querySelector('.check-icon')) {
            tab.insertAdjacentHTML('afterbegin', '<svg class="check-icon" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>');
          }
        } else if (tabStep === activeStep) {
          tab.classList.add('rs-panel-calc__progress-tab--active');
          tab.querySelector('.check-icon')?.remove();
        } else {
          tab.classList.add('rs-panel-calc__progress-tab--pending');
          tab.querySelector('.check-icon')?.remove();
        }
      });
    }

    _updateStepPreview(step) {
      const valueEl = this.querySelector(`[data-rs-calc-step="${step}"] [data-rs-calc-preview-value]`);
      if (!valueEl) return;

      switch (step) {
        case 1: {
          const wallArea = this.wallHeight * this.wallWidth;
          if (this.units === 'imperial') {
            valueEl.textContent = `${this.wallHeight.toFixed(1)} ft × ${this.wallWidth.toFixed(1)} ft (${wallArea.toFixed(0)} sq ft)`;
          } else {
            const hm = this.wallHeight * 0.3048;
            const wm = this.wallWidth * 0.3048;
            valueEl.textContent = `${hm.toFixed(2)} m × ${wm.toFixed(2)} m (${(hm * wm).toFixed(1)} m²)`;
          }
          break;
        }
        case 2:
          if (this.selectedSize) {
            valueEl.textContent = `${this.selectedSize.name} — ${this.selectedSize.panelsNeeded} panels`;
          }
          break;
        case 3:
          valueEl.textContent = `${this.variantsNeeded} × ${this._qtyLabel} (${this.totalPanels} panels, ${(this.wasteFactor * 100).toFixed(0)}% waste)`;
          break;
      }
    }

    /* ── Dynamic Rendering ────────────────────────────────────────────── */

    _renderSizeCards() {
      this._calculateSizeRecommendations();
      const container = this.querySelector('[data-rs-calc-size-cards]');
      if (!container) return;

      container.innerHTML = this.sizeRecommendations.map((size, idx) => {
        const isRecommended = idx === 0;
        const selected = this.selectedSize?.name === size.name ? ' rs-panel-calc__size-card--selected' : '';
        const costLabel = size.costPerSqFt < Infinity ? `${this._formatMoney(size.costPerSqFt)}/sq ft` : '—';

        return `
          <div class="rs-panel-calc__size-card${selected}" data-rs-calc-action="select-size" data-rs-calc-size="${this._escAttr(size.name)}">
            <div class="rs-panel-calc__size-badge">
              ${isRecommended ? `
                <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                Best Value
              ` : 'Large Format'}
            </div>
            <p class="rs-panel-calc__size-name">${size.name}</p>
            <div class="rs-panel-calc__size-row">
              <span class="rs-panel-calc__size-row-label">Panels needed</span>
              <span class="rs-panel-calc__size-row-value">${size.panelsNeeded}</span>
            </div>
            <div class="rs-panel-calc__size-row">
              <span class="rs-panel-calc__size-row-label">Price efficiency</span>
              <span class="rs-panel-calc__size-row-value rs-panel-calc__size-row-value--good">${costLabel}</span>
            </div>
            <div class="rs-panel-calc__size-row">
              <span class="rs-panel-calc__size-row-label">Waste</span>
              <span class="rs-panel-calc__size-row-value">${size.wastePercent.toFixed(1)}%</span>
            </div>
          </div>
        `;
      }).join('');
    }

    /**
     * Step 3 node. `data-set-panels`: HTML-escaped JSON array (lean variant objects: id, option1–3, price, available)
     * or numeric ids. If absent or empty, falls back to productData.variants from [data-rs-calc-product].
       */
    _getDataVariantHolder() {
      return this.querySelector('[data-rs-calc-step="3"]');
    }

    /** Parses `data-set-panels` — JSON array (browser decodes &quot; etc. from the attribute). */
    _parseSetPanelsAttr(holder) {
      const raw = holder?.getAttribute?.('data-set-panels');
      if (raw == null || String(raw).trim() === '') return [];
      try {
        const data = JSON.parse(String(raw).trim());
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    }

    /**
     * First variant per distinct (option1, option2), ordered by `data-set-panels` (full objects or ids).
     */
    _getUniqueOption1Option2PanelsFromHolder() {
      const dataVariantHolder = this._getDataVariantHolder();
      const raw = this._parseSetPanelsAttr(dataVariantHolder);
      const variantById = new Map(
        (this.productData?.variants || []).map((v) => [Number(v.id), v])
      );
      const order =
        raw.length > 0
          ? raw
          : (this.productData?.variants || []).map((v) => v.id);
      const seenOption1Option2 = new Set();
      const uniqueOption1Option2Panels = [];
      for (const item of order) {
        let row = null;
        if (item != null && typeof item === 'object' && !Array.isArray(item) && item.id != null) {
          row = {
            id: Number(item.id),
            option1: item.option1,
            option2: item.option2,
            price: item.price,
            available: Boolean(item.available)
          };
        } else {
          const id = Number(item);
          if (!Number.isFinite(id)) continue;
          const v = variantById.get(id);
          if (!v) continue;
          row = {
            id: Number(v.id),
            option1: v.option1,
            option2: v.option2,
            price: v.price,
            available: Boolean(v.available)
          };
        }
        if (!row) continue;
        const pairKey = `${String(row.option1 ?? '')}\0${String(row.option2 ?? '')}`;
        if (seenOption1Option2.has(pairKey)) continue;
        seenOption1Option2.add(pairKey);
        uniqueOption1Option2Panels.push(row);
      }
      return uniqueOption1Option2Panels;
    }

    /**
     * Match holder row to selected calculator size + optimal pack count.
     * Products differ: some use option1 = pack / option2 = size (legacy); many use option1 = size / option2 = pack (e.g. Adira).
     * Uses `_optionKeys.sizeKey` / `qtyKey` when resolved; otherwise tries both orientations.
     */
    _findHolderPanelRowByPackAndSize(panels, packCount, sizeName) {
      if (!panels?.length || sizeName == null || sizeName === '') return null;
      const nTarget = Number(packCount);
      if (!Number.isFinite(nTarget)) return null;

      const { sizeKey, qtyKey } = this._optionKeys || {};
      const rowMatches = (sizeVal, packVal) => {
        if (String(sizeVal ?? '') !== String(sizeName)) return false;
        const packN = this._packCountFromHolderOption(String(packVal ?? ''));
        if (packN != null) return packN === nTarget;
        // No pack column (option2/3 null — single Shopify option): treat row as one panel-unit SKU when pack count is 1.
        return nTarget === 1;
      };

      if (sizeKey && qtyKey) {
        const hit = panels.find((p) => rowMatches(p[sizeKey], p[qtyKey]));
        if (hit) return hit;
      }

      return (
        panels.find((p) => {
          if (rowMatches(p.option1, p.option2)) return true;
          if (rowMatches(p.option2, p.option1)) return true;
          return false;
        }) || null
      );
    }

    /** Pack line: match holder row by selected size + pack count; form variant only helps infer pack when optimiser state is missing. */
    _getPackRowForPricingFromHolder(variant) {
      const panels = this._getUniqueOption1Option2PanelsFromHolder();
      const sizeName = this.selectedSize?.name || '';
      let packCount =
        this._bestPanelSetPackSize != null ? this._bestPanelSetPackSize : null;
      if (packCount == null && variant) {
        const { qtyKey } = this._optionKeys || {};
        const candidates = [];
        if (qtyKey) candidates.push(variant[qtyKey]);
        candidates.push(variant.option2, variant.option1);
        for (let i = 0; i < candidates.length; i++) {
          const c = candidates[i];
          if (c == null || c === '') continue;
          const parsed = this._packCountFromHolderOption(String(c));
          if (parsed != null) {
            packCount = parsed;
            break;
          }
        }
      }
      if (packCount == null || !Number.isFinite(Number(packCount))) {
        const q = this._qtyLabel;
        if (q != null && q !== '') {
          const asNum = Number(q);
          packCount = Number.isFinite(asNum)
            ? asNum
            : this._packCountFromHolderOption(String(q));
        }
      }
      if (packCount == null || !Number.isFinite(Number(packCount))) return null;
      return this._findHolderPanelRowByPackAndSize(panels, packCount, sizeName);
    }

    /** Prefer checked variant radio, then hidden id input (theme-dependent). Scoped to this product column. */
    _getMainFormVariantInput() {
      const scope =
        this.closest('.product.js-product') ||
        this.closest('.product') ||
        this.closest('.shopify-section') ||
        document;
      const form =
        scope.querySelector('product-form form.js-product-form-main') ||
        scope.querySelector('form.js-product-form-main') ||
        document.querySelector('product-form form.js-product-form-main') ||
        document.querySelector('form.js-product-form-main');
      if (!form) return null;
      const checked = form.querySelector('input[name="id"]:checked');
      if (checked) return checked;
      const hidden = form.querySelector('input[name="id"][type="hidden"]');
      if (hidden) return hidden;
      return form.querySelector('input[name="id"]');
    }

    _getMainFormVariantId() {
      const input = this._getMainFormVariantInput();
      const n = parseInt(String(input?.value ?? '').trim(), 10);
      return Number.isFinite(n) ? n : null;
    }

    _findVariantById(id) {
      if (id == null) return null;
      const n = Number(id);
      return (this.productData?.variants || []).find((v) => Number(v.id) === n) ?? null;
    }

    _updateSetBreakdown() {
      const container = this.querySelector('[data-rs-calc-set-breakdown]');
      if (!container) return;

      const basePanels = this.selectedSize?.panelsNeeded || 0;
      const panelsWithWaste = Math.ceil(basePanels * (1 + this.wasteFactor));
      const wasteAllowancePanels = Math.ceil(basePanels * this.wasteFactor);
      /** Physical panels to buy (coverage + waste). Must match rows 1–2; do NOT use ceil(panelsWithWaste / panelsPerVariant) — that was SKU count and broke pack math. */
      const V = Math.max(0, Math.floor(panelsWithWaste));

      const uniqueOption1Option2Panels = this._getUniqueOption1Option2PanelsFromHolder();

      // Panel Set options: custom-select `data-value` when dropdown; swatches have no option list — use holder rows.
      const panelSetNumbersFromDataValues =
        this._collectPanelSetPackNumbersForOptimizer(uniqueOption1Option2Panels);
      // Descending: larger packs first (better value); e.g. 6, 3, 1
      this._panelSetPackNumbersFromDataValues = panelSetNumbersFromDataValues;
      const packSizes = this._panelSetPackNumbersFromDataValues;
      // Ignore pack size 1 only when other sizes exist (e.g. 1+3+6 → optimize with 3,6).
      // If 1 is the only option, keep it so the calculator still works for single-panel-only products.
      const multiPackSizes =
        packSizes.length === 1 ? packSizes : packSizes.filter((n) => n !== 1); 
      let bestPackSize = null;
      let bestResidue = Infinity;
      let bestPackCount = 0;
      if (multiPackSizes.length && V > 0) {
        // Exact fit: prefer largest n (cheapest per panel), e.g. 6 over 3 when both divide V
        const exactFit = multiPackSizes.filter((n) => V % n === 0).sort((a, b) => b - a);
        if (exactFit.length) {
          const n = exactFit[0];
          bestPackSize = n;
          bestPackCount = V / n;
          bestResidue = 0;
        } else {
          for (const n of multiPackSizes) {
            const packs = Math.floor(V / n);
            if (packs < 1) continue;
            const totalFromPacks = packs * n;
            const residue = V - totalFromPacks;
            if (
              residue < bestResidue ||
              (residue === bestResidue && bestPackSize != null && n > bestPackSize)
            ) {
              bestResidue = residue;
              bestPackSize = n;
              bestPackCount = packs;
            }
          }
          if (bestPackSize == null) {
            bestResidue = -Infinity;
            for (const n of multiPackSizes) {
              const packs = Math.ceil(V / n);
              const totalFromPacks = packs * n;
              const residue = V - totalFromPacks;
              if (
                residue > bestResidue ||
                (residue === bestResidue && bestPackSize != null && n > bestPackSize)
              ) {
                bestResidue = residue;
                bestPackSize = n;
                bestPackCount = packs;
              }
            }
          }
        }
      }
      // No Panel Set picker on PDP (size × finish only, etc.): optimise as V × 1-panel units so pricing / labels don't show null.
      if (V > 0 && bestPackSize == null) {
        bestPackSize = 1;
        bestPackCount = V;
        bestResidue = 0;
      }

      this._bestPanelSetPackSize = bestPackSize;
      this._bestPanelSetPackCount = bestPackCount;
      this._bestPanelSetResidue = bestPackSize != null ? bestResidue : null;

      const singlePanelCount =
        bestPackSize != null &&
        bestResidue !== null &&
        bestResidue !== Infinity &&
        bestResidue > 0
          ? bestResidue
          : 0;

      const sizeName = this.selectedSize?.name || '';
      const { sizeKey, qtyKey } = this._optionKeys || {};
      const singlePanelEntry = uniqueOption1Option2Panels.find((p) => {
        if (!sizeName) return false;
        if (sizeKey && qtyKey) {
          if (String(p[sizeKey] ?? '') !== String(sizeName)) return false;
          return /single/i.test(String(p[qtyKey] ?? ''));
        }
        const o1 = String(p.option1 ?? '');
        const o2 = String(p.option2 ?? '');
        if (/single/i.test(o2) && o1 === String(sizeName)) return true;
        if (/single/i.test(o1) && o2 === String(sizeName)) return true;
        return false;
      });
      const singlePanelUnitPrice = singlePanelEntry ? singlePanelEntry.price / 100 : null;

      this._singlePanelCount = singlePanelCount;
      this._singlePanelUnitPrice =
        singlePanelCount > 0 && singlePanelUnitPrice != null ? singlePanelUnitPrice : null;
      this._singlePanelVariantId =
        singlePanelCount > 0 && singlePanelEntry ? singlePanelEntry.id : null;

      const packPanelsTotal = bestPackSize != null ? bestPackCount * bestPackSize : 0;
      this.totalPanels = packPanelsTotal + singlePanelCount;

      const panelArea = this.selectedSize ? this.selectedSize.widthFt * this.selectedSize.heightFt : 0;
      const wallArea = this.wallHeight * this.wallWidth; 
      const leftover = (this.totalPanels * panelArea) - wallArea;

      this._qtyLabel = bestPackSize != null ? String(bestPackSize) : V > 0 ? '1' : '0';
      this.variantsNeeded = bestPackCount;

      const singlePanelsRowHtml =
        singlePanelCount > 0
          ? `
        <div class="rs-panel-calc__set-row single-panels">
          <span class="rs-panel-calc__set-row-label">Single panel</span>
          <span class="rs-panel-calc__set-row-value">× ${singlePanelCount}</span>
        </div>`
          : '';

      container.innerHTML = ` 
        <div class="rs-panel-calc__set-row">
          <span class="rs-panel-calc__set-row-label">Panels for coverage</span>
          <span class="rs-panel-calc__set-row-value">${basePanels}</span>
        </div>
        <div class="rs-panel-calc__set-row">
          <span class="rs-panel-calc__set-row-label">Waste allowance (${(this.wasteFactor * 100).toFixed(0)}%)</span>
          <span class="rs-panel-calc__set-row-value">+${wasteAllowancePanels}</span>
        </div>
        <div class="rs-panel-calc__set-divider"></div>
        <div class="rs-panel-calc__set-row">
          <span class="rs-panel-calc__set-row-label">${this._qtyLabel} Panel Set</span>
          <span class="rs-panel-calc__set-row-value">× ${this.variantsNeeded}</span>
        </div> 
        ${singlePanelsRowHtml}
        <div class="rs-panel-calc__set-row">
          <span class="rs-panel-calc__set-total-label">Total panels</span>
          <span class="rs-panel-calc__set-total-value">${this.totalPanels}</span>
        </div>
        <div class="rs-panel-calc__set-divider"></div>
        <div class="rs-panel-calc__set-row">
          <span class="rs-panel-calc__set-row-label">Leftover material</span>
          <span class="rs-panel-calc__set-row-value">${leftover.toFixed(1)} sq ft</span>
        </div>
      `;
    }

    /* ── Step 4: Price Breakdown ──────────────────────────────────────── */

    _updatePriceBreakdown() {
      const container = this.querySelector('[data-rs-calc-price-breakdown]');
      if (!container) return;

      if (!this.selectedSize || (this._hasStyleOptions() && !this.selectedStyle)) {
        container.innerHTML = '';
        container.style.display = 'none';
        return;
      }

      const variant = this._findVariantById(this._getMainFormVariantId());
      const packRow = this._getPackRowForPricingFromHolder(variant);
      if (!variant && !packRow) {
        container.innerHTML = '<p style="font-size:13px;color:var(--rs-calc-text-secondary);margin:0;">This combination is unavailable.</p>';
        container.style.display = 'flex';
        return;
      }

      const unitPrice =
        packRow && Number.isFinite(packRow.price)
          ? packRow.price / 100
          : variant
            ? variant.price / 100
            : 0;
      let totalPrice = unitPrice * this.variantsNeeded;
      const sc = this._singlePanelCount || 0;
      const su = this._singlePanelUnitPrice;
      if (sc > 0 && su != null) totalPrice += su * sc;

      const singlePriceRow =
        sc > 0
          ? `
        <div class="rs-panel-calc__price-row">
          <span class="rs-panel-calc__price-row-label">Single panel × ${sc}</span>
          <span class="rs-panel-calc__price-row-value">${
            su != null ? `${this._formatMoney(su)} each` : '—'
          }</span>
        </div>`
          : '';

      container.innerHTML = `
        <div class="rs-panel-calc__price-row">
          <span class="rs-panel-calc__price-row-label">${this._qtyLabel} Panel Set × ${this.variantsNeeded}</span>
          <span class="rs-panel-calc__price-row-value">${this._formatMoney(unitPrice)} each</span>
        </div>
        ${singlePriceRow}
        <div class="rs-panel-calc__price-divider"></div>
        <div class="rs-panel-calc__price-total">
          <span>Total</span>
          <span>${this._formatMoney(totalPrice)}</span>
        </div>
      `;
      container.style.display = 'flex';
    }

    _calculateTotalPrice() {
      if (!this.selectedSize || (this._hasStyleOptions() && !this.selectedStyle)) return 0; 
      const variant = this._findVariantById(this._getMainFormVariantId());
      const packRow = this._getPackRowForPricingFromHolder(variant);
      if (!variant && !packRow) return 0;
      const unitPrice =
        packRow && Number.isFinite(packRow.price)
          ? packRow.price / 100
          : variant
            ? variant.price / 100
            : 0;
      let total = unitPrice * this.variantsNeeded;
      const sc = this._singlePanelCount || 0;
      const su = this._singlePanelUnitPrice;
      if (sc > 0 && su != null) total += su * sc; 
      return total;
    }

    _renderStyleGrid() {
      const container = this.querySelector('[data-rs-calc-style-grid]');
      if (!container) return;

      container.innerHTML = this.parsedStyles.map(style => {
        const selected = this.selectedStyle === style.name ? ' rs-panel-calc__style-card--selected' : '';
        const imageHTML = style.image
          ? `<img class="rs-panel-calc__style-card-image" src="${style.image}" alt="${this._escAttr(style.name)}" loading="lazy">`
          : `<div class="rs-panel-calc__style-card-placeholder"></div>`;

        return `
          <div class="rs-panel-calc__style-card${selected}" data-rs-calc-action="select-style" data-rs-calc-style="${this._escAttr(style.name)}">
            ${imageHTML}
            <div class="rs-panel-calc__style-card-label">${style.name}</div>
          </div>
        `;
      }).join('');
    }

    _updateATCButton() {
      const btn = this.querySelector('[data-rs-calc-action="add-to-cart"]');
      if (!btn) return;

      const styleOk = !this._hasStyleOptions() || this.selectedStyle;
      if (styleOk && this.variantsNeeded > 0) {
        const totalPrice = this._calculateTotalPrice();
        const priceText = this._formatMoney(totalPrice);
        btn.textContent = `Add to Cart — ${priceText}`;
        btn.disabled = false;
        this._syncStickyPrice(priceText);
        this._syncPdpPrice(priceText, totalPrice);
      } else {
        btn.textContent =
          this._hasStyleOptions() && !this.selectedStyle
            ? 'Select a style to continue'
            : 'Add to Cart';
        btn.disabled = true;
        this._restorePdpPrice();
      }
    }

    /** Keep sticky bar price in sync with the calculator ATC total. */
    _syncStickyPrice(priceText) {
      document.querySelectorAll('#sticky-container .sticky-container__atc-price').forEach((el) => {
        el.setAttribute('data-calc-sticky-locked', 'true');
        el.textContent = priceText;
      });
    }

    _pdpPriceRoot() {
      return document.getElementById('pdp-price-container') || document.querySelector('.product-info__price');
    }

    _pdpPriceNodes(root) {
      return [...root.querySelectorAll('.ht-money, .price__current .js-value, .price__was .js-value, .price__default .js-value')]
        .filter((n) => !n.closest('.rs-pps'));
    }

    _parseMoney(text) {
      const raw = String(text || '').replace(/[^0-9.,]/g, '').replace(/,/g, '');
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : null;
    }

    _ensurePdpPriceSnapshot(root) {
      const existing = root.__calcPriceNodes;
      if (existing && existing[0] && !document.contains(existing[0])) {
        delete root.dataset.calcPriceSnapshot;
        root.__calcPriceNodes = null;
      }
      if (root.dataset.calcPriceSnapshot && root.__calcPriceNodes) return;

      const nodes = this._pdpPriceNodes(root);
      root.__calcPriceNodes = nodes;
      root.dataset.calcPriceSnapshot = JSON.stringify(nodes.map((n) => n.textContent));
    }

    /** Mirror calculator ATC total onto the main PDP price. */
    _syncPdpPrice(priceText, totalAmount) {
      const root = this._pdpPriceRoot();
      if (!root || !totalAmount || totalAmount <= 0) {
        this._restorePdpPrice(root);
        return;
      }

      this._ensurePdpPriceSnapshot(root);
      root.setAttribute('data-calc-price-locked', 'true');

      const nodes = root.__calcPriceNodes || [];
      const originals = JSON.parse(root.dataset.calcPriceSnapshot || '[]');

      let primaryOrig = null;
      nodes.forEach((node, i) => {
        if (node.closest('.price--strike, .price__was')) return;
        const n = this._parseMoney(originals[i]);
        if (n != null && n > 0 && primaryOrig == null) primaryOrig = n;
      });
      const ratio = primaryOrig > 0 ? totalAmount / primaryOrig : null;

      nodes.forEach((node, i) => {
        if (node.closest('.price--strike, .price__was')) {
          const unit = this._parseMoney(originals[i]);
          if (unit != null && ratio != null) node.textContent = this._formatMoney(unit * ratio);
          return;
        }
        node.textContent = priceText;
      });
    }

    _restorePdpPrice(root) {
      const el = root || this._pdpPriceRoot();
      if (!el) return;

      const nodes = el.__calcPriceNodes || [];
      const originals = el.dataset.calcPriceSnapshot ? JSON.parse(el.dataset.calcPriceSnapshot) : null;
      if (originals) {
        nodes.forEach((node, i) => {
          if (originals[i] != null) node.textContent = originals[i];
        });
      }

      el.removeAttribute('data-calc-price-locked');
      delete el.dataset.calcPriceSnapshot;
      el.__calcPriceNodes = null;
    }

    /* ── Confirmation & Error ─────────────────────────────────────────── */

    _showConfirmation() {
      const confirm = this.querySelector('[data-rs-calc-confirm]');
      if (confirm) {
        const textEl = confirm.querySelector('.rs-panel-calc__confirm-text');
        const subEl = confirm.querySelector('.rs-panel-calc__confirm-sub');
        if (textEl) textEl.textContent = 'Added to Cart!';
        if (subEl) {
          const packLine = `${this.variantsNeeded} × ${this._qtyLabel} Panel Set`;
          const sc = this._singlePanelCount || 0;
          const singleLine =
            sc > 0
              ? ` and ${sc} single panel${sc === 1 ? '' : 's'}`
              : '';
          subEl.textContent = `${packLine}${singleLine} added successfully`;
        }
        confirm.classList.add('rs-panel-calc__confirm--visible');
      }
    }

    _showError(message) {
      const errorEl = this.querySelector('[data-rs-calc-error]');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('rs-panel-calc__error--visible');
        setTimeout(() => errorEl.classList.remove('rs-panel-calc__error--visible'), 5000);
      }
    }

    /* ── Utilities ────────────────────────────────────────────────────── */

    _escAttr(str) {
      return String(str).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/'/g,'&#39;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    }

    _formatMoney(amount) {
      return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  customElements.define('rs-panel-calculator', RS_PanelCalculator);
}